import ButtonT from '@components/tailwind-components/Button/ButtonT';
import CheckboxInputT from '@components/tailwind-components/Input/CheckboxInputT';
import InputWithLabelT from '@components/tailwind-components/Input/InputWithLabelT';
import SelectInputWithLabelT from '@components/tailwind-components/Input/SelectInputWithLabelT';
import LabelT from '@components/tailwind-components/Label/LabelT';
import FileUploadT from '@components/tailwind-components/Upload/FileUploadT';
import type { CreateCompanyDTO } from '@conversed/rest/src/contracts/company/company.contract';
import { COUNTRIES } from '@conversed/shared/src/central/constants/countries';
import { VALID_BOT_LANGUAGES } from '@conversed/shared/src/central/constants/languages';
import { VAT_REGEX } from '@conversed/shared/src/central/constants/regex';
import type { TFile } from '@conversed/shared/src/central/types/common.type';
import {
  STUDIO_TYPE_LABELS,
  StudioType,
} from '@conversed/shared/src/central/types/studio.type';
import { useCompanySelector } from '@hooks/useCompanySelector';
import type React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface Props {
  onSubmit: (data: CreateCompanyDTO['company']) => void;
}

const CreateCompanyForm: React.FC<Props> = ({ onSubmit }) => {
  const { handleSubmit, control, watch } = useForm<CreateCompanyDTO['company']>(
    {
      shouldUseNativeValidation: true,
      defaultValues: {
        defaultLanguage: 'en',
        country: 'NL',
        studioType: StudioType.CHAT_AI,
        provision: false,
      },
    },
  );

  const { t } = useTranslation();
  const [companyIcon, setCompanyIcon] = useState<TFile | null>(null);
  const { getParentCompanySelection } = useCompanySelector();
  // * With the toggle off the admin has already built the instance by hand, so the
  // * form collects the two values the company document cannot work without.
  const isProvisioning = watch('provision');

  const parentCompanyOptions = useMemo(() => {
    return getParentCompanySelection();
  }, [getParentCompanySelection]);

  const languageOptions = VALID_BOT_LANGUAGES.map(([value, description]) => ({
    value,
    description,
  }));

  const studioTypeOptions = Object.entries(STUDIO_TYPE_LABELS).map(
    ([value, label]) => ({
      value,
      description: label,
    }),
  );

  const handleFormSubmit = useCallback(
    (data: CreateCompanyDTO['company']) => {
      onSubmit({
        ...data,
        // * The parent-company select yields '' when left unset; send undefined so
        // * the optional parentCompanyId is not submitted as an empty ObjectId string.
        parentCompanyId: data.parentCompanyId || undefined,
        icon: companyIcon,
      });
    },
    [companyIcon, onSubmit],
  );

  const handleFileUpload = (file: TFile | string) => {
    if (typeof file !== 'string' && 'base64' in file) {
      return setCompanyIcon(file);
    }
    return setCompanyIcon(null);
  };

  return (
    <form className='w-full' onSubmit={handleSubmit(handleFormSubmit)}>
      <div className='grid grid-cols-1 gap-x-6 gap-y-2'>
        <div className='col-span-1'>
          <InputWithLabelT
            type='text'
            field='name'
            placeholder={t('new-customer.company.name')}
            label={t('new-customer.company.name')}
            control={control}
            options={{
              required: t('general.validation.required'),
            }}
          />
        </div>

        <div className='col-span-1'>
          <SelectInputWithLabelT
            field='studioType'
            label={t('new-customer.company.studio-type')}
            values={studioTypeOptions}
            control={control}
            options={{
              required: t('general.validation.required'),
            }}
            noOptionSelectedText={t('new-customer.company.select-studio-type')}
          />
        </div>

        <div className='col-span-1'>
          <SelectInputWithLabelT
            field='parentCompanyId'
            label={t('new-customer.company.parent-company')}
            values={parentCompanyOptions}
            control={control}
            noOptionSelectedText={t('new-customer.company.new-parent-company')}
          />
          <p className='text-pico text-gray-500 mt-1 mb-2 italic'>
            {t('new-customer.company.parent-company-hint')}
          </p>
        </div>

        <div className='col-span-1'>
          <LabelT text={t('new-customer.company.upload-icon')} />
          <p className='text-xs italic mt-3'>
            {t('component.upload.by.imageUrl.description')}
          </p>
          <FileUploadT accept={['image/*']} onFileUpload={handleFileUpload} />
        </div>

        <div className='col-span-1'>
          <SelectInputWithLabelT
            field='defaultLanguage'
            label={t('new-customer.company.default-language')}
            control={control}
            values={languageOptions}
          />
        </div>

        <div className='col-span-1'>
          <InputWithLabelT
            type='text'
            field='vat'
            placeholder={t('new-customer.company.vat')}
            label={t('new-customer.company.vat')}
            control={control}
            options={{
              pattern: {
                value: VAT_REGEX,
                message: t('general.validation.invalid-format'),
              },
            }}
          />
        </div>

        <div className='col-span-1'>
          <InputWithLabelT
            type='text'
            field='websiteUrl'
            placeholder={t('new-customer.company.website')}
            label={t('new-customer.company.website')}
            control={control}
          />
        </div>

        <div className='col-span-1'>
          <SelectInputWithLabelT
            field='country'
            label={t('new-customer.company.country')}
            control={control}
            values={COUNTRIES}
          />
        </div>

        <div className='col-span-1'>
          <CheckboxInputT
            id='provision'
            field='provision'
            control={control}
            label={t('new-customer.company.provision')}
          />
          <p className='text-pico text-gray-500 mt-1 mb-2 italic'>
            {t('new-customer.company.provision-hint')}
          </p>
        </div>

        {!isProvisioning && (
          <>
            <div className='col-span-1'>
              <InputWithLabelT
                type='text'
                field='botBackendUrl'
                placeholder={t('new-customer.company.bot-backend-url')}
                label={t('new-customer.company.bot-backend-url')}
                control={control}
                options={{
                  required: t('general.validation.required'),
                }}
              />
            </div>

            <div className='col-span-1'>
              <InputWithLabelT
                type='text'
                field='bucket'
                placeholder={t('new-customer.company.bucket')}
                label={t('new-customer.company.bucket')}
                control={control}
                options={{
                  required: t('general.validation.required'),
                }}
              />
              <p className='text-pico text-gray-500 mt-1 mb-2 italic'>
                {t('new-customer.company.manual-instance-hint')}
              </p>
            </div>
          </>
        )}
      </div>

      <div className='flex justify-end'>
        <ButtonT
          type='submit'
          styling='primary'
          size='xs'
          rounding='md'
          className='mt-3'>
          {t('general.buttons.next')} &rarr;
        </ButtonT>
      </div>
    </form>
  );
};

export default CreateCompanyForm;
